import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  COLOR_BACKGROUND_2,
  COLOR_ERROR_1,
  COLOR_ERROR_1_HOVER,
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_2,
  COLOR_SUCCESS_1,
  COLOR_TERTIARY_1_100,
  COLOR_TEXT_2,
} from 'src/utils/colors';
import { callApi } from 'src/core/callApi';

/** Neutral card surface to reduce pastel-heavy look. */
const KPI_CARD_SURFACE = '#FFFFFF';

const kpiFormCardSx = {
  minHeight: 550,
  width: { xs: '100%', sm: 450 },
  p: 2.5,
  borderRadius: 2,
  border: `1px solid rgba(0, 0, 0, 0.1)`,
  bgcolor: KPI_CARD_SURFACE,
  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.07)',
} as const;

/** Subtle input fill for cleaner contrast on white cards. */
const kpiOutlinedInputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#F8FAFC',
    boxShadow: 'inset 0 1px 2px rgba(15, 23, 42, 0.04)',
  },
};

const Kpi = ({ adminPassword }) => {
  const [affiliatePartnerEmailID, setAffiliatePartnerEmailID] = useState<string>('');
  const [affiliatePartnerName, setAffiliatePartnerName] = useState<string>('');
  const [affiliatePartnerKey, setAffiliatePartnerKey] = useState<string>('');
  const [showEmailOrNameMissingAlert, setShowEmailOrNameMissingAlert] = useState<boolean>(false);
  const [showAffiliateKeyLoader, setShowAffiliateKeyLoader] = useState<boolean>(false);
  const [promocodeName, setPromocodeName] = useState<string>('');
  const [promocodeDiscount, setPromocodeDiscount] = useState<string>('');
  const [promocodeFrequency, setPromocodeFrequency] = useState<string>('forever');
  const [promocodeOperation, setPromocodeOperation] = useState<string>('create');
  const [showDetailsMissingAlertInCouponActivation, setShowDetailsMissingAlertInCoupon] = useState<
    boolean
  >(false);
  const [couponApiSuccessResponse, setCouponApiSuccessResponse] = useState<string>('');
  const [isCouponActivationLoading, setCouponActivationLoading] = useState<boolean>(false);
  const [couponCodeError, setCouponCodeError] = useState<string>('');

  useEffect(() => {
    setCouponApiSuccessResponse('');
    setCouponCodeError('');
  }, [promocodeOperation]);

  const fetchApi = async (url, queryParams, retry = 0) => {
    if (retry > 5) return null;
    try {
      const res = await callApi(url, { queryParams }, false);
      const resString = JSON.stringify(res);
      if (resString == '{}') {
        throw new Error('Timeout');
      }
      return res;
    } catch (err) {
      console.log('Error: ', err);
      return fetchApi(url, queryParams, retry + 1);
    }
  };

  const handleAffiliatePartnerAccountCreation = async () => {
    setShowAffiliateKeyLoader(true);
    setShowEmailOrNameMissingAlert(false);
    const url =
      'https://a8q9o0b2r3.execute-api.us-east-2.amazonaws.com/default/createAffiliatePartner';
    const config = {
      queryParams: {
        admin_password: encodeURIComponent(adminPassword),
        name: affiliatePartnerName,
        email: encodeURIComponent(affiliatePartnerEmailID),
      },
    };
    if (affiliatePartnerName && affiliatePartnerEmailID) {
      const res = await callApi(url, config, false);
      setAffiliatePartnerKey(res.partnerKey);
      setShowEmailOrNameMissingAlert(false);
      setShowAffiliateKeyLoader(false);
    } else {
      setShowEmailOrNameMissingAlert(true);
      setShowAffiliateKeyLoader(false);
    }
  };

  const handleCouponActivationAndDeletion = async () => {
    setCouponActivationLoading(true);
    setCouponApiSuccessResponse('');
    setCouponCodeError('');
    if (
      (promocodeOperation == 'create' &&
        !promocodeDiscount &&
        !promocodeName &&
        !promocodeFrequency) ||
      (promocodeOperation == 'delete' && !promocodeName)
    ) {
      setShowDetailsMissingAlertInCoupon(true);
      setCouponActivationLoading(false);
    } else {
      setShowDetailsMissingAlertInCoupon(false);
      const url =
        'https://61psikryqf.execute-api.us-east-2.amazonaws.com/default/activateCouponCode';
      const couponActivationConfig = {
        queryParams: {
          admin_password: encodeURIComponent(adminPassword),
          discountedPer: promocodeDiscount,
          duration: encodeURIComponent(promocodeFrequency),
          couponName: encodeURIComponent(promocodeName),
          // isTest: true,
          operation: 'create',
        },
      };
      const couponDeletionConfig = {
        queryParams: {
          admin_password: encodeURIComponent(adminPassword),
          couponName: encodeURIComponent(promocodeName),
          // isTest: true,
          operation: 'delete',
        },
      };
      let res;
      if (promocodeOperation == 'create') {
        res = await callApi(url, couponActivationConfig, false);
      } else {
        res = await callApi(url, couponDeletionConfig, false);
      }

      if (res.message) {
        setCouponApiSuccessResponse(res.message);
        setCouponActivationLoading(false);
        setCouponCodeError('');
      }
      if (res.error) {
        setCouponApiSuccessResponse('');
        setCouponActivationLoading(false);
        setCouponCodeError(res.error);
      }
    }
  };

  return (
    <Box
      sx={{
        height: '95%',
        width: '100%',
        margin: '10px',
        overflowY: 'scroll',
      }}
    >
      <Box sx={{ display: 'flex', gap: 5, flexDirection: 'column', height: '100%' }}>
        {/* MARKETING KPI */}
        <Box>
          {/* MARKETING */}
          <Box sx={{ textAlign: 'center', margin: '10px' }}>
            <Typography variant="h4" sx={{ color: COLOR_TEXT_2, fontWeight: 600 }}>
              Marketing KPIs
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 5,
              padding: '10px',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            {/* Affiliate Partner Onboarding */}
            <Paper variant="outlined" sx={kpiFormCardSx}>
              <Box
                sx={{
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h6" sx={{ color: COLOR_TEXT_2 }}>
                  Onboard Affiliate Partners
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: COLOR_TEXT_2,
                    gap: 2,
                  }}
                >
                  <Typography>Name:</Typography>
                  <TextField
                    id="outlined-basic"
                    label=""
                    variant="outlined"
                    size="small"
                    value={affiliatePartnerName}
                    onChange={e => {
                      setAffiliatePartnerName(e.target.value);
                    }}
                    sx={kpiOutlinedInputSx}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: COLOR_TEXT_2,
                    gap: 2,
                  }}
                >
                  <Typography>Email:</Typography>
                  <TextField
                    id="outlined-basic"
                    label=""
                    variant="outlined"
                    size="small"
                    value={affiliatePartnerEmailID}
                    onChange={e => {
                      setAffiliatePartnerEmailID(e.target.value);
                    }}
                    sx={kpiOutlinedInputSx}
                  />
                </Box>
                <Button
                  sx={{
                    backgroundColor: COLOR_PRIMARY_1,
                    cursor: 'pointer',
                    color: '#fff',
                    '&:hover': { backgroundColor: COLOR_PRIMARY_2 },
                  }}
                  onClick={handleAffiliatePartnerAccountCreation}
                >
                  Create Affiliate Account
                </Button>

                {showEmailOrNameMissingAlert && (
                  <Alert severity="error">Please enter name and email Id.</Alert>
                )}

                {affiliatePartnerKey && (
                  <Alert severity="success">Generated Key is {affiliatePartnerKey}</Alert>
                )}

                {showAffiliateKeyLoader && <CircularProgress />}
              </Box>
            </Paper>

            {/* Activate Promo code */}
            <Paper variant="outlined" sx={kpiFormCardSx}>
              <Box
                sx={{
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                >
                  <Button
                    sx={{ backgroundColor: COLOR_SUCCESS_1 }}
                    onClick={() => setPromocodeOperation('create')}
                  >
                    <Typography variant="body1" sx={{ color: '#fff', borderRadius: '10px' }}>
                      Activate Promo Code
                    </Typography>
                  </Button>
                  <Button
                    sx={{ backgroundColor: COLOR_ERROR_1 }}
                    onClick={() => setPromocodeOperation('delete')}
                  >
                    <Typography variant="body1" sx={{ color: '#fff', borderRadius: '10px' }}>
                      Delete Promo Code
                    </Typography>
                  </Button>
                </Box>

                {promocodeOperation == 'create' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: COLOR_TEXT_2,
                        gap: 2,
                      }}
                    >
                      <Typography>Coupon Name:</Typography>
                      <TextField
                        id="outlined-basic"
                        label=""
                        variant="outlined"
                        size="small"
                        value={promocodeName}
                        onChange={e => {
                          setPromocodeName(e.target.value);
                        }}
                        sx={kpiOutlinedInputSx}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: COLOR_TEXT_2,
                        gap: 2,
                      }}
                    >
                      <Typography>% Discount:</Typography>
                      <TextField
                        id="outlined-basic"
                        label=""
                        variant="outlined"
                        size="small"
                        value={promocodeDiscount}
                        onChange={e => {
                          setPromocodeDiscount(e.target.value);
                        }}
                        sx={kpiOutlinedInputSx}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: COLOR_TEXT_2,
                        gap: 2,
                      }}
                    >
                      <Typography>Frequency:</Typography>
                      <FormControl>
                        <Select
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          value={promocodeFrequency}
                          label=""
                          onChange={e => {
                            setPromocodeFrequency(e.target.value);
                          }}
                          sx={{
                            '& .MuiSelect-select': {
                              padding: '4px 0px 4px 10px',
                            },
                            marginRight: '10px',
                            backgroundColor: COLOR_BACKGROUND_2,
                            boxShadow: 'inset 0 1px 2px rgba(15, 23, 42, 0.04)',
                          }}
                        >
                          <MenuItem value={'forever'}>Forever</MenuItem>
                          <MenuItem value={'once'}>Once</MenuItem>
                          <MenuItem value={'3_Months'}>First 3 Months</MenuItem>
                          <MenuItem value={'6_Months'}>First 6 Months</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Button
                      sx={{
                        backgroundColor: COLOR_PRIMARY_1,
                        cursor: 'pointer',
                        color: '#fff',
                        '&:hover': { backgroundColor: COLOR_PRIMARY_2 },
                      }}
                      onClick={handleCouponActivationAndDeletion}
                    >
                      Activate Coupon
                    </Button>
                  </Box>
                )}
                {promocodeOperation == 'delete' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: COLOR_TEXT_2,
                        gap: 2,
                      }}
                    >
                      <Typography>Coupon Name:</Typography>
                      <TextField
                        id="outlined-basic"
                        label=""
                        variant="outlined"
                        size="small"
                        value={promocodeName}
                        onChange={e => {
                          setPromocodeName(e.target.value);
                        }}
                        sx={kpiOutlinedInputSx}
                      />
                    </Box>
                    <Button
                      sx={{
                        backgroundColor: COLOR_ERROR_1_HOVER,
                        cursor: 'pointer',
                        color: '#fff',
                        '&:hover': { backgroundColor: COLOR_ERROR_1_HOVER },
                      }}
                      onClick={handleCouponActivationAndDeletion}
                    >
                      Delete Coupon
                    </Button>
                  </Box>
                )}

                {showDetailsMissingAlertInCouponActivation && (
                  <Alert severity="error">Please enter all the details</Alert>
                )}

                {couponApiSuccessResponse && (
                  <Alert severity="success">{couponApiSuccessResponse}</Alert>
                )}

                {couponCodeError && <Alert severity="error">{couponCodeError}</Alert>}

                {isCouponActivationLoading && <CircularProgress />}
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* Useful Resources */}
        <Box>
          <Box sx={{ textAlign: 'center', margin: '10px' }}>
            <Typography variant="h4" sx={{ color: COLOR_TEXT_2, fontWeight: 600 }}>
              Useful Resources
            </Typography>
          </Box>
          <Paper
            variant="outlined"
            sx={{
              width: '95%',
              maxWidth: 900,
              mx: 'auto',
              p: 3,
              borderRadius: 2,
              border: `1px solid rgba(0, 0, 0, 0.1)`,
              bgcolor: KPI_CARD_SURFACE,
              boxShadow: '0 2px 8px rgba(15, 23, 42, 0.07)',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, marginBottom: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ color: COLOR_TEXT_2 }}>
                See All KPIs At Airbtics :
              </Typography>
              <Button
                onClick={() => {
                  window.open(
                    'https://www.notion.so/airbtics/KPIs-be29b7cc247648e9ac51fe11175bc181',
                    '_blank'
                  );
                }}
                sx={{ backgroundColor: COLOR_TERTIARY_1_100 }}
              >
                Airbtics KPIs
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, marginBottom: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ color: COLOR_TEXT_2 }}>
                See Survey Results :
              </Typography>
              <Button
                onClick={() => {
                  window.open(
                    'https://insights.hotjar.com/sites/1926311/surveys/responses/968108?sort_by=-index',
                    '_blank'
                  );
                }}
                sx={{ backgroundColor: COLOR_TERTIARY_1_100 }}
              >
                Survey Results
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, marginBottom: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ color: COLOR_TEXT_2 }}>
                Stripe Dashboard :
              </Typography>
              <Button
                onClick={() => {
                  window.open('https://dashboard.stripe.com/dashboard', '_blank');
                }}
                sx={{ backgroundColor: COLOR_TERTIARY_1_100 }}
              >
                Stripe
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, marginBottom: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ color: COLOR_TEXT_2 }}>
                Google Analytics Dashboard :
              </Typography>
              <Button
                onClick={() => {
                  window.open(
                    'https://analytics.google.com/analytics/web/#/p379520711/reports/reportinghub?params=_u..nav%3Dmaui',
                    '_blank'
                  );
                }}
                sx={{ backgroundColor: COLOR_TERTIARY_1_100 }}
              >
                Google Analytics
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default Kpi;
